import { useEffect, useState } from 'react';
import { MediaAnalyticsEvents } from '../../types';
import { Suggestion } from './Suggestion';
import Player from 'video.js/dist/types/player';
import { Flex, Space, Typography } from 'antd';
import { AwesomeDuoIcon } from '../icons';
import { CloseOutlined } from '@ant-design/icons';
import { useMediaQuery } from 'react-responsive';
import { CascadingText } from '../CascadingText';
import styled, { keyframes } from 'styled-components';
import { radius } from '@libertify/ui';
import { t } from 'i18next';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useChatSessionId } from '../../hooks/useChatSessionId';

export const SuggestionsOverlay = ({
  suggestions,
  setNextQuery,
  setLayout,
  playerRef,
}: {
  suggestions: string[] | undefined;
  setLayout: () => void;
  setNextQuery: (query: string) => void;
  playerRef: React.RefObject<Player>;
}) => {
  const [isVisible, setVisible] = useState(false);

  const isMobile = useMediaQuery({ maxWidth: 767 });
  const isMobileLandscape = useMediaQuery({
    query: '(max-width: 1000px) and (orientation: landscape)',
  });

  const isPhone = isMobile || isMobileLandscape;
  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const onEnded = () => {
      setVisible(true);
    };

    const checkPlayerReady = () => {
      const player = playerRef.current;
      if (
        player &&
        !player.isDisposed() &&
        typeof player.ready === 'function'
      ) {
        player.ready(() => {
          if (cancelled || player.isDisposed()) return;
          player.on('ended', onEnded);
        });
      } else if (!cancelled) {
        timerId = setTimeout(checkPlayerReady, 100);
      }
    };

    checkPlayerReady();

    return () => {
      cancelled = true;
      if (timerId) clearTimeout(timerId);
      const player = playerRef.current;
      if (player && !player.isDisposed()) {
        player.off('ended', onEnded);
      }
    };
  }, [playerRef]);

  const { trackEvent } = useAnalytics();
  const getChatSessionId = useChatSessionId();

  const handleClick = (query: string) => {
    setLayout();
    setNextQuery(query);
    trackEvent(MediaAnalyticsEvents.SuggestedQuerySelected, {
      query,
      chat_session_id: getChatSessionId(),
    });
    setVisible(false);
  };

  if (!isVisible) return;

  return (
    <SuggestionsWrapper>
      {suggestions && (
        <Flex
          className="suggestions-container"
          vertical
          style={
            isPhone
              ? {
                  position: 'absolute',
                  width: '100vw',
                  height: '100vh',
                  boxSizing: 'border-box',
                  zIndex: 100,
                  background:
                    'linear-gradient(180deg, rgba(0, 0, 0, 1), rgba(0, 0, 0, 0.2))',
                  padding: '20px',
                  transition: 'opacity 0.5s ease-in-out',
                  opacity: isVisible ? 1 : 0,
                }
              : {
                  position: 'absolute',
                  left: 20,
                  zIndex: 100,
                  maxWidth: '45vw',
                  bottom: '80px',
                  background: 'rgba(255,255,255,1)',
                  padding: '20px',
                  borderRadius: radius.box,
                  transition: 'opacity 0.5s ease-in-out',
                  opacity: isVisible ? 1 : 0,
                  boxShadow: '2px 2px 10px rgba(0, 0, 0, 0.2)',
                }
          }
        >
          <Flex
            style={isPhone ? { marginTop: '10vh' } : { marginBottom: 10 }}
            justify="space-between"
          >
            <Space style={isPhone ? { width: '80%' } : { display: 'flex' }}>
              <AwesomeDuoIcon
                stroke={isPhone ? 'white' : 'black'}
                width={20}
                height={20}
              />
              {isPhone ? (
                <Typography.Text
                  style={{
                    color: 'white',
                  }}
                >
                  {t('introSuggestedQueries')}
                </Typography.Text>
              ) : (
                <CascadingText
                  style={{
                    color: 'black',
                  }}
                  duration={500}
                >
                  <div>{t('introSuggestedQueries')}</div>
                </CascadingText>
              )}
            </Space>
            <CloseOutlined
              onClick={() => setVisible(false)}
              style={{ cursor: 'pointer', color: isPhone ? 'white' : 'black' }}
            />
          </Flex>
          <div
            style={{
              animation: 'fadeIn 0.5s ease-in-out',
              gap: 8,
              display: 'flex',
              flexDirection: isPhone ? 'column' : 'row',
              marginTop: 10,
            }}
          >
            {(suggestions || []).map((query: string, index: number) => (
              <Suggestion
                key={index}
                question={query}
                onSelect={() => handleClick(query)}
                highlighted={false}
              />
            ))}
          </div>
        </Flex>
      )}
    </SuggestionsWrapper>
  );
};

const borderAnimation = keyframes`
  0% {
    border-width: 4px;
    opacity: 0.8;
  }
  100% {
    border-width: 8px;
    opacity: 0;
  }
`;

const SuggestionsWrapper = styled.div`{
  background-color: transparent;

  .suggestions-container {
    position: relative;
    background-color: white; /* Background color of inner content */
    border-radius: inherit; /* Ensure it matches the parent */
    z-index: 1; /* Make sure content is above the border */
  }

  .suggestions-container::before {
    content: "";
    position: absolute;
    top: -4px;
    left: -4px;
    width: calc(100% + 8px);
    height: calc(100% + 8px);
    background: linear-gradient(58deg, rgb(26, 66, 216) 0%, rgb(244, 0, 206) 100%);
    border-radius: inherit;
    z-index: -1;
    animation: ${borderAnimation} 2s ease-out;
    opacity: 0;
  }
}`;
